import { createStore } from 'vuex';
import { markRaw } from 'vue';
import VuexPersistence from 'vuex-persist';
import { SelectedMarker } from '../models/PigRunState';
import { User } from '../models/User';
import { DatabaseServerUtcDate } from '../models/DatabaseServerUtcDate';
import { PigRunSummaryData } from '../models/PigRunSummaryData';
import PigRun from './modules/PigRun';
import Hubs from './modules/Hubs';
import CommandStore from './modules/CommandStore';
import AdminStore from './modules/AdminStore';
import DeviceStore from './modules/DeviceStore';
import PassageStore from './modules/PassageStore';
import RealtimeStreamingStore from './modules/RealtimeStreamingStore';
import MetricsStore from './modules/MetricsStore';
import DownloadSessionStore from './modules/DownloadSessionStore';
import GroupChatStore from './modules/GroupChatStore';
import MetricsChatStore from './modules/MetricsChatStore';
import StreamSessionStore from './modules/StreamSessionStore';
import PigRunService from '@/services/PigRunService';

let lastSoundPlayedAt = 0;
const SOUND_THROTTLE_MS = 5000;

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: (state: any) => {
    // Persist a plain user object that includes both `roles` (new) and
    // `role` (legacy) so that code reading the raw localStorage value
    // before User rehydration still works.
    let user = state.user;
    if (user) {
      user = {
        id: user.id,
        name: user.name,
        roles: user.roles ?? (user.role ? [user.role] : []),
        role: user.role ?? (user.roles ? user.roles[0] : ''),
        token: user.token,
        tokenExpiry: user.tokenExpiry,
      };
    }
    return {
      user,
      queryParams: state.queryParams,
      pigRun: {},
      notifications: state.notifications,
    };
  },
});

export default createStore({
  state: {
    // Login
    user: null,
    queryParams: null,

    // LayoutDefault
    leftDrawerOpen: true,

    showDeviceInfo: true,
    showDeviceTable: true,
    deviceListOnlineOnly: false,
    showPigRunManager: false,
    showReportManager: false,
    showDownloadManager: false,
    showBroadcastCommands: false,
    showStreamManager: false,
    reportManagerPreselectedPigRunId: null as number | null,

    // NotificationSettings - populated on startup from API (if empty)
    notifications: {},
    soundOptions: [
      'None',
      'Standard',
      'Space Dog',
      'Martian',
      'Magic',
      'Oringzz',
      'Bling',
    ],
    soundFiles: [
      '',
      '/notifications/when.mp3',
      '/notifications/out-of-space-dog.mp3',
      '/notifications/martian-gun.mp3',
      '/notifications/just-like-magic.mp3',
      '/notifications/oringz-w468.mp3',
      '/notifications/get-outta-here.mp3',
    ],

    // LeafletMap
    map: null,
    center: null,
    zoom: null,
    layerControl: null,

    // markers
    selectedMarker: {},

    databaseServerUtcDate: {},

    // Used by Default.vue to hold the list of active pig run id values for the logged in user that has notification configured.
    activePigRunArrayWithNotification: [],

    // Used by Default.vue to hold the list of active pig run uid values for the logged in user that has notification configured.
    activePigRunUidArrayWithNotification: [],

    // Tracks which pig run is currently plotted on the main map
    plottedPigRunId: null as number | null,
    plottedPigRunData: null as PigRunSummaryData | null,
  },

  mutations: {
    SET_DATABASE_SERVER_UTC_DATE(
      state: any,
      payload: DatabaseServerUtcDate | null
    ) {
      state.databaseServerUtcDate = payload;
    },
    DRAWER_TOGGLE(state: any) {
      state.leftDrawerOpen = !state.leftDrawerOpen;
    },
    LOGIN(state: any, user: User | null) {
      state.user = user;
    },
    SET_QUERY_PARAMS(state: any, params: any) {
      state.queryParams = params;
    },
    SET_SELECTED_MARKER(state: any, marker: SelectedMarker) {
      state.selectedMarker = marker;
    },

    SET_PLOTTED_PIG_RUN_ID(state: any, pigRunId: number | null) {
      state.plottedPigRunId = pigRunId;
      if (pigRunId === null) {
        state.plottedPigRunData = null;
      }
    },

    SET_PLOTTED_PIG_RUN_DATA(state: any, data: any) {
      state.plottedPigRunData = data;
    },

    TOGGLE_DEVICE_LIST_ONLINE_ONLY(state: any) {
      state.deviceListOnlineOnly = !state.deviceListOnlineOnly;
    },

    SHOW_PIG_RUN_MANAGER(state: any, payload: any) {
      state.showPigRunManager = true;
      state.showDeviceInfo = false;
      state.showDeviceTable = false;
      state.showReportManager = false;
      state.showDownloadManager = false;
      state.showBroadcastCommands = false;
      state.showStreamManager = false;
    },

    HIDE_PIG_RUN_MANAGER(state: any, payload: any) {
      state.showPigRunManager = false;
      state.showDeviceInfo = true;
      state.showDeviceTable = true;
      state.showReportManager = false;
      state.showDownloadManager = false;
      state.showBroadcastCommands = false;
      state.showStreamManager = false;
    },

    SHOW_REPORT_MANAGER(state: any, payload: any) {
      state.showPigRunManager = false;
      state.showDeviceInfo = false;
      state.showDeviceTable = false;
      state.showReportManager = true;
      state.showDownloadManager = false;
      state.showBroadcastCommands = false;
      state.showStreamManager = false;
      state.reportManagerPreselectedPigRunId = payload?.pigRunId ?? null;
    },

    SHOW_DOWNLOAD_MANAGER(state: any, payload: any) {
      state.showPigRunManager = false;
      state.showDeviceInfo = false;
      state.showDeviceTable = false;
      state.showReportManager = false;
      state.showDownloadManager = true;
      state.showBroadcastCommands = false;
      state.showStreamManager = false;
    },

    SHOW_BROADCAST_COMMANDS(state: any, payload: any) {
      state.showPigRunManager = false;
      state.showDeviceInfo = false;
      state.showDeviceTable = false;
      state.showReportManager = false;
      state.showDownloadManager = false;
      state.showBroadcastCommands = true;
      state.showStreamManager = false;
    },

    SHOW_STREAM_MANAGER(state: any) {
      state.showPigRunManager = false;
      state.showDeviceInfo = false;
      state.showDeviceTable = false;
      state.showReportManager = false;
      state.showDownloadManager = false;
      state.showBroadcastCommands = false;
      state.showStreamManager = true;
    },

    HIDE_STREAM_MANAGER(state: any) {
      state.showPigRunManager = false;
      state.showDeviceInfo = true;
      state.showDeviceTable = true;
      state.showReportManager = false;
      state.showDownloadManager = false;
      state.showBroadcastCommands = false;
      state.showStreamManager = false;
    },

    SET_LAYER_CONTROL(state: any, payload: any) {
      state.layerControl = payload ? markRaw(payload) : payload;
    },
    SAVE_NOTIFICATION_SETTINGS(state: any, payload: any) {
      state.notifications = payload;
    },
    SET_ACTIVE_PIG_RUN_ARRAY_WITH_NOTIFICATION(state: any, payload: any) {
      state.activePigRunArrayWithNotification = payload;
    },
    SET_ACTIVE_PIG_RUN_UID_ARRAY_WITH_NOTIFICATION(state: any, payload: any) {
      state.activePigRunUidArrayWithNotification = payload;
    },
  },
  actions: {
    drawerToggle(context) {
      context.commit('DRAWER_TOGGLE');
    },
    getDatabaseServerUtcDate(context) {
      PigRunService.getDatabaseServerUtcDate()
        .then((response) => {
          const databaseServerUtcDate: DatabaseServerUtcDate =
            new DatabaseServerUtcDate(response.data);
          context.commit('SET_DATABASE_SERVER_UTC_DATE', databaseServerUtcDate);
        })
        .catch((error) => {
          console.error(
            'Dispatch action = [getDatabaseServerUtcDate]. Error response.'
          );
        });
    },

    getActivePigRunListForUserWithNotification(context, userId: string) {
      PigRunService.getActivePigRunListForUserWithNotification(userId)
        .then((response) => {
          context.commit(
            'SET_ACTIVE_PIG_RUN_ARRAY_WITH_NOTIFICATION',
            response.data.pigRunIdList
          );
        })
        .catch((exception) => {
          console.error(
            'Exception caught in action dispatch = [getActivePigRunListForUserWithNotification]'
          );
        });
    },

    getActivePigRunUidListForUserWithNotification(context, userId: string) {
      PigRunService.getActivePigRunUidListForUserWithNotification(userId)
        .then((response) => {
          context.commit(
            'SET_ACTIVE_PIG_RUN_UID_ARRAY_WITH_NOTIFICATION',
            response.data.uidList
          );
        })
        .catch((exception) => {
          console.error(
            'Exception caught in action dispatch = [getActivePigRunUidListForUserWithNotification]. exception = ' +
              exception
          );
        });
    },

    actionOne(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    actionTwo(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    addDevice(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    removeDevice(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    changePassword(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    broadcastShow(context, agms) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    broadcastClose(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    login(context, user) {
      context.commit('LOGIN', user);
    },
    setSelectedMarker(context, marker: SelectedMarker) {
      context.commit('SET_SELECTED_MARKER', marker);
    },
    notify(context, message) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    notifySound(context, soundOption) {
      const now = Date.now();
      if (now - lastSoundPlayedAt < SOUND_THROTTLE_MS) return;

      const idx = this.state.soundOptions.indexOf(soundOption);
      if (idx !== -1 && idx !== 0 && idx < this.state.soundFiles.length) {
        lastSoundPlayedAt = now;
        new Audio(this.state.soundFiles[idx]).play();
      }
    },
    setLayerControl(context, payload) {
      context.commit('SET_LAYER_CONTROL', payload);
    },
    notificationSettings(context, payload) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    saveNotificationSettings(context, payload) {
      context.commit('SAVE_NOTIFICATION_SETTINGS', payload);
      this.dispatch('notify', 'Notification preferences saved');
    },
    getDefaultNotificationSettings(context, payload) {
      // TODO this should hit an API for admin configured settings when available
      if (Object.keys(this.state.notifications).length === 0) {
        const mockApiResult = {
          heartbeat: false,
          passage: false,
          passageSound: '',
          support: false,
          supportSound: '',
          powerMode: false,
          powerModeSound: '',
          recordMode: false,
          recordModeSound: '',
          frequencySelect: false,
          frequencySelectSound: '',
          streamEnable: false,
          streamEnableSound: '',
          detectionCount: false,
          detectionCountSound: '',
          deviceInfo: false,
          deviceInfoSound: '',
          gpsFix: false,
          gpsFixSound: '',
          controlSetting: false,
          controlSettingSound: '',
          error: false,
          errorSound: '',
          offline: false,
          offlineSound: '',
          shutdown: false,
          shutdownSound: '',
          sleepReport: false,
          sleepReportSound: '',
        };
        context.commit('SAVE_NOTIFICATION_SETTINGS', mockApiResult);
      }
    },
    localUserManagerShow(context, payload) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    setQueryParams(context, payload) {
      context.commit('SET_QUERY_PARAMS', payload);
    },
    handleQueryParams(context, payload) {
      if (
        Object.prototype.hasOwnProperty.call(payload, 'lat') &&
        Object.prototype.hasOwnProperty.call(payload, 'lon')
      ) {
        context.dispatch('goToLatLon', payload);
      }
      context.commit('SET_QUERY_PARAMS', null);
    },
    goToLatLon(context, payload) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    selectPigRunLayer(context, pigRunId: number) {
      // Signal-only: LeafletMap subscribes and switches the active map layer.
      // Also keeps selectedPigRunId in sync.
      context.commit('SET_SELECTED_PIG_RUN_ID', pigRunId);
    },
    ClearCompletedRun(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    RefreshPigRunsList(context) {
      // Signal-only: PigRunManager subscribes to refresh the pig runs list
    },
  },

  getters: {
    token: (state) => {
      if (state.user && state.user.token) {
        return state.user.token;
      }
      return '';
    },
    user: (state) => {
      return state.user;
    },
  },

  plugins: [vuexLocal.plugin],

  modules: {
    pigRun: PigRun,
    hubs: Hubs,
    command: CommandStore,
    admin: AdminStore,
    device: DeviceStore,
    passage: PassageStore,
    realtimeStreaming: RealtimeStreamingStore,
    metrics: MetricsStore,
    downloadSession: DownloadSessionStore,
    groupChat: GroupChatStore,
    metricsChat: MetricsChatStore,
    streamSession: StreamSessionStore,
  },
});
